// Public Layout - Navigation and footer for customer-facing pages
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import Cart from './Cart';

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { getCartCount, openCart } = useCart();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: '/shop' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const navLinkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? 'text-sage-700 font-semibold' : 'text-ink-soft hover:text-sage-700'}`;

  return (
    <div className="min-h-screen flex flex-col bg-cream-100 text-ink">
      {/* Header */}
      <header className="bg-cream-50/95 backdrop-blur border-b border-cream-300 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt=""
                className="h-10 sm:h-12 w-auto object-contain"
              />
              <span className="font-serif text-xl sm:text-2xl font-semibold text-ink leading-none whitespace-nowrap">
                Magnolia Mold Co.
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigation.map((item) => (
                <NavLink key={item.name} to={item.href} end={item.href === '/'} className={navLinkClass}>
                  {item.name}
                </NavLink>
              ))}

              <span className="h-5 w-px bg-cream-300" aria-hidden="true" />

              {/* Account Links */}
              {user ? (
                <Link
                  to="/account"
                  className="flex items-center gap-2 text-sm text-ink-soft hover:text-sage-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Account</span>
                </Link>
              ) : (
                <div className="flex items-center gap-5">
                  <Link to="/customer-login" className="text-sm text-ink-soft hover:text-sage-700 transition-colors">
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="text-sm px-4 py-2 border border-sage-700 text-sage-700 rounded-full hover:bg-sage-700 hover:text-cream-50 transition-colors"
                  >
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Admin Link (if admin logged in) */}
              {user && (
                <Link to="/admin" className="text-xs text-ink-muted hover:text-ink-soft">
                  Admin
                </Link>
              )}

              <CartButton count={getCartCount()} onClick={openCart} />
            </div>

            {/* Mobile: cart + menu button */}
            <div className="flex items-center gap-1 md:hidden">
              <CartButton count={getCartCount()} onClick={openCart} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-ink-soft hover:text-sage-700"
                aria-label="Toggle menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7h16M4 12h16M4 17h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-cream-300">
              <div className="flex flex-col gap-4">
                {navigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.href === '/'}
                    onClick={() => setMobileMenuOpen(false)}
                    className={navLinkClass}
                  >
                    {item.name}
                  </NavLink>
                ))}
                {user ? (
                  <>
                    <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="text-sm text-ink-soft hover:text-sage-700">
                      Account
                    </Link>
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="text-sm text-ink-muted hover:text-sage-700">
                      Admin
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/customer-login" onClick={() => setMobileMenuOpen(false)} className="text-sm text-ink-soft hover:text-sage-700">
                      Sign In
                    </Link>
                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="text-sm text-ink-soft hover:text-sage-700">
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-sage-900 text-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="" className="h-10 w-auto" />
                <span className="font-serif text-2xl text-cream-50">Magnolia Mold Co.</span>
              </div>
              <p className="text-sm text-cream-300/80 max-w-xs leading-relaxed">
                Handcrafted earrings made with love and attention to detail. Each piece is unique and special.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-gold-300 mb-4">Explore</h3>
              <ul className="space-y-2 text-sm">
                {navigation.slice(1).map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-cream-300/80 hover:text-cream-50 transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-gold-300 mb-4">Say Hello</h3>
              <ul className="space-y-2 text-sm text-cream-300/80">
                <li><a href="mailto:magnoliamolds26@gmail.com" className="hover:text-cream-50 transition-colors break-all">magnoliamolds26@gmail.com</a></li>
                <li><a href="tel:+12297330303" className="hover:text-cream-50 transition-colors">(229) 733-0303</a></li>
              </ul>
              <div className="flex gap-4 mt-5">
                <a href="https://www.facebook.com/profile.php?id=61586223724947" className="text-cream-300/80 hover:text-cream-50 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/magnoliamoldco" className="text-cream-300/80 hover:text-cream-50 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Made & Managed By */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] text-gold-300 mb-4">Site By</h3>
              <a
                href="https://root-foundry.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block opacity-70 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/root.png"
                  alt="Root Foundry"
                  className="h-10 w-auto filter brightness-0 invert"
                />
              </a>
            </div>
          </div>

          <div className="border-t border-sage-700 mt-12 pt-8 text-center text-cream-300/60 text-xs tracking-wide">
            <p>&copy; {new Date().getFullYear()} Magnolia Mold Co. Handmade with love, despite the toddler fits.</p>
          </div>
        </div>
      </footer>

      {/* Shopping Cart Drawer */}
      <Cart />
    </div>
  );
}

function CartButton({ count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative p-2 text-ink-soft hover:text-sage-700 transition-colors"
      aria-label={`Open cart (${count} items)`}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-gold-500 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-semibold">
          {count}
        </span>
      )}
    </button>
  );
}
